import { useEffect, useRef, useState } from 'react';
import {
  tapFeedback,
  tapFeedbackSubtle,
  zikrDoneFeedback,
} from '../../telegram/haptics';
import { ru } from '../../strings/ru';
import styles from './TasbihCounter.module.css';

/** Выше этой кратности частая вибрация утомляет — переходим на деликатную */
const SUBTLE_HAPTIC_THRESHOLD = 20;

interface Props {
  done: number;
  total: number;
  onTap: () => boolean;
  onComplete: () => void;
}

export function TasbihCounter({ done, total, onTap, onComplete }: Props) {
  const [pulse, setPulse] = useState(0);
  const completeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const remaining = Math.max(0, total - done);
  const isDone = remaining === 0;
  const progress = total > 0 ? done / total : 0;

  useEffect(
    () => () => {
      if (completeTimer.current) clearTimeout(completeTimer.current);
    },
    [],
  );

  function handleTap() {
    if (isDone) {
      onComplete();
      return;
    }

    const finished = onTap();

    // Визуальный отклик обязателен всегда: на десктопе и в вебе
    // хаптика — no-op, и без пульса тап вообще никак не отзовётся.
    setPulse((p) => p + 1);

    if (finished) {
      zikrDoneFeedback();
      completeTimer.current = setTimeout(onComplete, 420);
    } else if (total > SUBTLE_HAPTIC_THRESHOLD) {
      tapFeedbackSubtle();
    } else {
      tapFeedback();
    }
  }

  const R = 54;
  const circumference = 2 * Math.PI * R;

  return (
    <div className={styles.wrap}>
      <button
        className={styles.target}
        onClick={handleTap}
        data-done={isDone}
        aria-label={
          isDone
            ? ru.session.next
            : `${ru.session.tapToCount}, ${ru.session.remaining} ${remaining}`
        }
      >
        <span key={pulse} className={styles.ripple} aria-hidden="true" />

        <svg className={styles.ring} viewBox="0 0 128 128" aria-hidden="true">
          <circle
            className={styles.ringTrack}
            cx="64"
            cy="64"
            r={R}
            fill="none"
            strokeWidth="6"
          />
          <circle
            className={styles.ringFill}
            cx="64"
            cy="64"
            r={R}
            fill="none"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - progress)}
            transform="rotate(-90 64 64)"
          />
        </svg>

        <span className={styles.label}>
          {isDone ? (
            <span className={styles.check} aria-hidden="true">
              ✓
            </span>
          ) : (
            <>
              <span className={styles.count}>{remaining}</span>
              <span className={styles.of}>
                {ru.session.of} {total}
              </span>
            </>
          )}
        </span>
      </button>

      <p className={styles.hint}>
        {isDone ? ru.session.next : ru.session.tapToCount}
      </p>
    </div>
  );
}
